import { usePermission } from "./useAccessPermissions";
import { useEffectiveOrgPermissions } from "./useEffectiveOrgPermission";
import { PERMISSIONS } from "utils/accessManagement/permissionKeys";

// Per-domain capability hooks built on top of useEffectiveOrgPermissions/
// PERMISSIONS. Call sites read by intent ("can I update a user on THIS org")
// instead of repeating raw permission strings — a component destructures
// only the flags it needs.
//
// Every check here is already scoped to `orgId`: for a per-org role
// (OrganizationOwner/OrganizationApprover/Leadership) it checks THAT
// SPECIFIC role's own permissions[], not the user's global cross-org union —
// see useEffectiveOrgPermission's own comment for why that distinction is
// load-bearing (e.g. Owner-of-a-different-org must not leak user:id:update
// onto an org where the same person is only Leadership). The 3 real
// cross-org-admin roles (EnterpriseAdmin/OperationsAdmin/OperationsUser)
// still resolve via the global union, since they have no org_id to scope to.
//
// orgId is required — every call site has one (from useParams()), since
// these hooks are only ever used on an org-scoped page/component.

export const useUserCapabilities = (orgId) => {
  const [canUpdateUser, canViewCostCenter, canCreateUser, canBulkCreateUser, canBulkUpdateUser] =
    useEffectiveOrgPermissions(orgId, [
      PERMISSIONS.USER_ID_UPDATE,
      PERMISSIONS.USER_COSTCENTER_READ,
      PERMISSIONS.USER_CREATE,
      PERMISSIONS.USER_BULK_CREATE,
      PERMISSIONS.USER_BULK_UPDATE,
    ]);
  return { canUpdateUser, canViewCostCenter, canCreateUser, canBulkCreateUser, canBulkUpdateUser };
};

export const useOrganizationCapabilities = (orgId) => {
  const [canReadOrganization, canReadOrganizationById, canUpdateOrganizationById, hasAnyOrganizationIdAccess] =
    useEffectiveOrgPermissions(orgId, [
      PERMISSIONS.ORGANIZATION_READ,
      PERMISSIONS.ORGANIZATION_ID_READ,
      PERMISSIONS.ORGANIZATION_ID_UPDATE,
      // Required-side wildcard: true if the role grants ANY organization:id:*
      // permission (read, update, or a future one), not just an exact match.
      PERMISSIONS.ORGANIZATION_ID_WILDCARD,
    ]);
  return { canReadOrganization, canReadOrganizationById, canUpdateOrganizationById, hasAnyOrganizationIdAccess };
};

export const useRoleAssignmentCapabilities = (orgId) => {
  const [canReadRoleAssignment, canCreateRoleAssignment, canUpdateRoleAssignment] = useEffectiveOrgPermissions(
    orgId,
    [PERMISSIONS.ROLE_ASSIGNMENTS_READ, PERMISSIONS.ROLE_ASSIGNMENTS_CREATE, PERMISSIONS.ROLE_ASSIGNMENTS_UPDATE]
  );
  return { canReadRoleAssignment, canCreateRoleAssignment, canUpdateRoleAssignment };
};

// Usage-read bundle shared by every "view a user's Copilot usage before
// acting on their budget" call site (BulkChangeBudgetModal, UlbRequestsTable).
export const useCopilotUsageCapabilities = (orgId) => {
  const [canViewUsage, canViewUsageById, canViewModelUsage, canViewUsageByCostCenter, canViewUsageByCostCenterId] =
    useEffectiveOrgPermissions(orgId, [
      PERMISSIONS.COPILOT_USAGE_READ,
      PERMISSIONS.COPILOT_USAGE_ID_READ,
      PERMISSIONS.COPILOT_MODEL_READ,
      PERMISSIONS.COPILOT_USAGE_COSTCENTER_READ,
      PERMISSIONS.COPILOT_USAGE_COSTCENTER_ID_READ,
    ]);
  return { canViewUsage, canViewUsageById, canViewModelUsage, canViewUsageByCostCenter, canViewUsageByCostCenterId };
};

export const useCopilotBudgetCapabilities = (orgId) => {
  const [
    canCreateBudget,
    canUpdateBudget,
    canReadBudget,
    canReadBudgetById,
    canReadBudgetRequests,
    canCreateBudgetRequest,
    canReviewBudgetRequest,
  ] = useEffectiveOrgPermissions(orgId, [
    PERMISSIONS.COPILOT_BUDGET_CREATE,
    // Direct budget writes (e.g. the Joined tab's bulk "Change Budget" button)
    // upsert a ULB row — create if the user has none yet, update if they
    // already do — so both create and id:update are required unconditionally,
    // same reasoning as the request-review pair below.
    PERMISSIONS.COPILOT_BUDGET_ID_UPDATE,
    PERMISSIONS.COPILOT_BUDGET_READ,
    PERMISSIONS.COPILOT_BUDGET_ID_READ,
    PERMISSIONS.COPILOT_BUDGET_REQUEST_READ,
    // Approving a request updates an existing ULB OR creates a brand-new one
    // server-side, depending on whether the requester already had one — the
    // frontend can't know which in advance for a bulk selection, so both are
    // required unconditionally rather than branching per-row.
    PERMISSIONS.COPILOT_BUDGET_REQUEST_ID_CREATE,
    PERMISSIONS.COPILOT_BUDGET_REQUEST_ID_UPDATE,
  ]);
  return {
    canCreateBudget,
    canUpdateBudget,
    canReadBudget,
    canReadBudgetById,
    canReadBudgetRequests,
    canCreateBudgetRequest,
    canReviewBudgetRequest,
  };
};

export const useCopilotDashboardSettingsCapabilities = (orgId) => {
  const [canViewDashboardSettings, canUpdateDashboardSettings] = useEffectiveOrgPermissions(orgId, [
    PERMISSIONS.COPILOT_SETTINGS_READ,
    PERMISSIONS.COPILOT_DASHBOARD_SETTING_UPDATE,
  ]);
  return { canViewDashboardSettings, canUpdateDashboardSettings };
};

// Active/inactive Copilot users for the billing cycle (GET /users/copilot/users/activity).
export const useCopilotActivityCapabilities = (orgId) => {
  const [canViewActivity] = useEffectiveOrgPermissions(orgId, [PERMISSIONS.COPILOT_ACTIVITY_READ]);
  return { canViewActivity };
};

export const useCopilotExportCapabilities = (orgId) => {
  const [canViewExport, canCreateUsageExport, canCreateBudgetExport, canReadExportJob, canDownloadExportJob] =
    useEffectiveOrgPermissions(orgId, [
      PERMISSIONS.COPILOT_EXPORT_READ,
      PERMISSIONS.COPILOT_EXPORT_CREATE,
      PERMISSIONS.COPILOT_BUDGET_EXPORT_CREATE,
      PERMISSIONS.COPILOT_EXPORT_JOB_READ,
      PERMISSIONS.COPILOT_EXPORT_JOB_DOWNLOAD,
    ]);
  return { canViewExport, canCreateUsageExport, canCreateBudgetExport, canReadExportJob, canDownloadExportJob };
};

// Metered usage, org-scoped tier (an org's own billing usage). Each section of the page has its own read permission.
export const useMeteredUsageCapabilities = (orgId) => {
  const [canViewSummary, canViewTrend, canViewProducts, canViewUsers, canViewUserDetail, canViewCostCenters, canViewMovers, canViewQuality, canViewExport, canCreateExport, canReadExportJob, canDownloadExportJob] =
    useEffectiveOrgPermissions(orgId, [
      PERMISSIONS.METERED_USAGE_SUMMARY_READ,
      PERMISSIONS.METERED_USAGE_TREND_READ,
      PERMISSIONS.METERED_USAGE_PRODUCTS_READ,
      PERMISSIONS.METERED_USAGE_USERS_READ,
      PERMISSIONS.METERED_USAGE_USER_ID_READ,
      PERMISSIONS.METERED_USAGE_COSTCENTERS_READ,
      PERMISSIONS.METERED_USAGE_MOVERS_READ,
      PERMISSIONS.METERED_USAGE_QUALITY_READ,
      PERMISSIONS.METERED_USAGE_EXPORT_READ,
      PERMISSIONS.METERED_USAGE_EXPORT_CREATE,
      PERMISSIONS.METERED_USAGE_EXPORT_JOB_READ,
      PERMISSIONS.METERED_USAGE_EXPORT_JOB_DOWNLOAD,
    ]);
  return { canViewSummary, canViewTrend, canViewProducts, canViewUsers, canViewUserDetail, canViewCostCenters, canViewOrganizations: false, canViewMovers, canViewQuality, canViewExport, canCreateExport, canReadExportJob, canDownloadExportJob };
};

// Metered usage, enterprise-wide tier (every organization). A separate set of permissions from the org tier — a
// user can hold one without the other, so callers check which they have. These are global permissions (the
// ECR Ops portal has no org to scope to).
export const useMeteredUsageEnterpriseCapabilities = () => {
  const canViewSummary = usePermission(PERMISSIONS.METERED_USAGE_ENTERPRISE_SUMMARY_READ);
  const canViewTrend = usePermission(PERMISSIONS.METERED_USAGE_ENTERPRISE_TREND_READ);
  const canViewProducts = usePermission(PERMISSIONS.METERED_USAGE_ENTERPRISE_PRODUCTS_READ);
  const canViewUsers = usePermission(PERMISSIONS.METERED_USAGE_ENTERPRISE_USERS_READ);
  const canViewUserDetail = usePermission(PERMISSIONS.METERED_USAGE_ENTERPRISE_USER_ID_READ);
  const canViewCostCenters = usePermission(PERMISSIONS.METERED_USAGE_ENTERPRISE_COSTCENTERS_READ);
  const canViewOrganizations = usePermission(PERMISSIONS.METERED_USAGE_ENTERPRISE_ORGANIZATIONS_READ);
  const canViewMovers = usePermission(PERMISSIONS.METERED_USAGE_ENTERPRISE_MOVERS_READ);
  const canViewQuality = usePermission(PERMISSIONS.METERED_USAGE_ENTERPRISE_QUALITY_READ);
  const canViewExport = usePermission(PERMISSIONS.METERED_USAGE_EXPORT_READ);
  const canCreateExport = usePermission(PERMISSIONS.METERED_USAGE_ENTERPRISE_EXPORT_CREATE);
  const canReadExportJob = usePermission(PERMISSIONS.METERED_USAGE_EXPORT_JOB_READ);
  const canDownloadExportJob = usePermission(PERMISSIONS.METERED_USAGE_EXPORT_JOB_DOWNLOAD);
  return { canViewSummary, canViewTrend, canViewProducts, canViewUsers, canViewUserDetail, canViewCostCenters, canViewOrganizations, canViewMovers, canViewQuality, canViewExport, canCreateExport, canReadExportJob, canDownloadExportJob };
};
